import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { BsQrCodeScan } from "react-icons/bs"

import { privateRoutes } from "router/routes"
import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useAuth, useModal, useBikeStoreProcess, useTheme } from "hooks"

import FormElement from "components/Form/FormElement"
import { ModalScanCode } from "components/Elements/ScanOcode"
import { ScanIcon } from "components/Elements/Icons"
import BikeStatusInformation from "./Partials/BikeStatusInformation"
import { isNotEmpty } from "utils/validation"

import { sanitizeCode, isBikeCodeValid } from "utils"
import {
	anyOperators,
	MC_OPERATOR,
	BC_OPERATOR,
	MFC_OPERATOR,
	BIKE_ID_MAX_LENGTH,
	ACTIVE_OPERATOR_STATUS,
} from "utils/constants"
import { HOME_SEARCH } from "features/bike/SearchBike"
import { QRCodeModal } from "../QRCodeModal"
import { Bike, ErrorResponse } from "models"

const defaultFormEvents = {
	ready: true,
}

const reorderOperatorArray = (operatorArray) => {
	const bicycodeIndex = operatorArray.findIndex((item) => item === BC_OPERATOR)
	const reorderedUserOperators =
		bicycodeIndex > -1
			? operatorArray.map((item, index) => {
					// Reorder so the 1st index is BC
					const newIndex =
						index + bicycodeIndex >= operatorArray.length
							? index + bicycodeIndex - operatorArray.length
							: index + bicycodeIndex
					return operatorArray[newIndex]
				})
			: operatorArray
	return reorderedUserOperators
}

type FormSearchBikeProps = {
	isAdmin: boolean
	readonly?: boolean
	setBikeAvailableForPortability?: (bike: Bike) => void
	context?: string
}

export const FormSearchBike: React.FC<FormSearchBikeProps> = ({
	isAdmin,
	readonly = false,
	setBikeAvailableForPortability,
	context = "",
}) => {
	const location = useLocation()
	const { bikeProcessState } = useBikeStoreProcess()
	const { currentBike: bike, bikeStatus } = bikeProcessState
	const defaultFormFields = {
		bikeCode: "",
	}
	const [formFields, setFormFields] = React.useState(defaultFormFields)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
	const { entity, logout } = useAuth()
	const [operators, setOperators] = React.useState([])
	const { currentTheme } = useTheme()

	const [isQRCodeModalOpen, setIsQRCodeModalOpen] = React.useState(false)

	const isMoustacheCurrentTheme = currentTheme.id_api === MC_OPERATOR

	React.useEffect(() => {
		// operator must be set
		if (!operators.length) return
		// Link example : ?bikeId=MF257FEC4F from HistoryTable
		const urlParams = new URLSearchParams(location?.search)
		const bikeId = urlParams.get("bikeCode")

		// If we have an url param => we set the data in formfields + start the search
		if (isNotEmpty(bikeId)) {
			setFormFields((s) => ({
				...s,
				bikeCode: bikeId,
			}))
			handleSubmit(bikeId)
		}

		// FormSearchBike component displays on multiple parent components
		// We must specify a context to not report an error with setBikeAvailableForPortability
		if (context === HOME_SEARCH) {
			// If no data => reset input
			if (!isNotEmpty(bikeId) || !isNotEmpty(urlParams)) {
				setFormFields((s) => ({
					...s,
					bikeCode: "",
				}))
				setApiError(null)
				setBikeAvailableForPortability(null)
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, operators, bike])

	React.useEffect(() => {
		if (isNotEmpty(bike)) {
			setFormFields((s) => ({
				...s,
				bikeCode: bike.code,
			}))
		}

		if (entity) {
			const userOperators = entity?.entity_data?.entity_permission.operateurs
			let reorderedUserOperators = []
			if (typeof userOperators !== "string") {
				// operators are saved as an array in the shop entities
				reorderedUserOperators = reorderOperatorArray(userOperators)
			} else {
				// operators are saved as string in the admin entities
				reorderedUserOperators =
					userOperators === "ANY" ? reorderOperatorArray(anyOperators) : [userOperators]
			}
			setOperators(reorderedUserOperators)
		}
	}, [entity, bike])

	const [apiError, setApiError] = React.useState(null)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const { alert } = useAlert()
	const { showModal } = useModal()
	const { t } = useTranslation()
	const navigate = useNavigate()

	// set operators on search placeholder
	const searchPlaceholder = "CODE"

	const handleValidation = React.useCallback(
		(name, errs) => {
			// this method must be prevent if bikeCode params in URL
			const urlParams = new URLSearchParams(location?.search)
			const bikeId = urlParams.get("bikeCode")
			if (!isNotEmpty(bikeId)) {
				setErrors((s) => {
					const cleanErrors = [...s.filter((e) => e.origin !== name)]
					return [...cleanErrors, ...errs]
				})
			}
		},
		[location]
	)

	const onQRCodeScanned = async (result) => {
		setIsQRCodeModalOpen(false)
		const moustacheBaseUrl = "moustachebikes.com"

		const isResultMCUrl = result.includes(moustacheBaseUrl)
		if (!isResultMCUrl) alert("error", t("searchBike.invalidQrcode", { ns: "bike" }))

		if (isResultMCUrl) {
			const splitUrl = result.split("/")
			const lastSegment = splitUrl[splitUrl.length - 1]

			const isValidCode = isBikeCodeValid(lastSegment)
			if (!isValidCode) alert("error", t("code_min_length", { ns: "form" }))

			if (isValidCode) {
				setFormFields((s) => ({
					...s,
					bikeCode: lastSegment,
				}))
				handleSubmit(lastSegment, true)
			}
		}
	}

	const handleSubmit = async (bikeId: string, byPassErrors = false) => {
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (formReady || byPassErrors) {
			try {
				// hide error
				setShowLocalErrors(false)
				setApiError(null)
				// prevent form to be submitted again
				setFormEvents({
					...formEvents,
					ready: false,
				})
				const { res: bike, getError } = await BikeApi.getBike(
					sanitizeCode(bikeId).trim().toUpperCase(),
					"byBikeId"
				)

				if (getError()) throw getError()

				if (operators.includes(bike.operator)) {
					// Remove query params if history.goBack() from the next page
					navigate({ pathname: location?.pathname })
					navigate({
						pathname: `${privateRoutes.bike.basePath}/${bike?.bike_id}`,
					})
				} else {
					setApiError("api:search_error_TooManyRequestsException")
					// bike_status !== null => the bike has an owner
					// The current entity must have BC as one of these operators

					const isAvailableOperator =
						isNotEmpty(entity.data_operators) &&
						Boolean(
							entity.data_operators.find(
								(operator) =>
									(operator.operator === BC_OPERATOR ||
										operator.operator === MFC_OPERATOR) &&
									operator.status === ACTIVE_OPERATOR_STATUS
							)
						)

					if (bike.bike_status !== null && isAvailableOperator) {
						// Sended bike data to portability block and so display the block
						setBikeAvailableForPortability(bike)
					} else {
						// Need to clear previous info if user make new search without reload the page
						setBikeAvailableForPortability(null)
					}
				}
			} catch (error) {
				console.error(error)
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)

				if (
					["BikeNotFoundException", "InvalidArgument"].includes(
						(error as ErrorResponse)?.code
					)
				)
					setApiError(erroMsg)
				if (["NotAuthorizedException"].includes((error as ErrorResponse)?.code)) logout()
			} finally {
				setFormEvents((s) => ({
					...s,
					ready: true,
				}))
			}
		} else {
			setShowLocalErrors(true)
		}
	}

	const scanCode = () => {
		showModal(ModalScanCode, {
			callback: async ({ ocode }) => {
				try {
					const { res: bike, getError } = await BikeApi.getBike(ocode, "byOcodeId")
					if (getError()) throw getError()

					setFormFields((s) => ({
						...s,
						bikeCode: bike.bike_id,
					}))
					// Redirect to bike view
					navigate(`${privateRoutes.bike.basePath}/${bike?.bike_id}`)
				} catch (error) {
					console.error(error)
					const erroMsg = (error as ErrorResponse)?.message?.toString()
					if (erroMsg) alert("error", erroMsg)

					if (
						["BikeNotFoundException", "InvalidArgument"].includes(
							(error as ErrorResponse)?.code
						)
					)
						setApiError(erroMsg)
					if (["NotAuthorizedException"].includes((error as ErrorResponse)?.code))
						logout()
				}
			},
		})
	}

	return (
		<>
			{bike && (bikeStatus === null || bikeStatus?.toString()) && (
				<h2 className={clsx("c-h2 font-semibold mb-7 uppercase", { "c-h3": isMobileOnly })}>
					{(() => {
						switch (bikeStatus) {
							case -1: // unknown
								return t("status.unknown", { ns: "bike" })
							case 1: // sold
								return t("status.sold", { ns: "bike" })
							case 2: // lost, stolen
								return t("status.stolen", { ns: "bike" })
							case 3: // destroyed
								return t("status.destroyed", { ns: "bike" })
							case 4: // searching for owner
								return t("status.searching_owner", { ns: "bike" })
							case 5: // in sale
								return t("status.in_sale_by_owner", { ns: "bike" })
							case 6: // back_in_store
								return t("status.to_register", { ns: "bike" })
							default: // in_store
								return t("status.to_register", { ns: "bike" })
						}
					})()}
				</h2>
			)}

			<div>
				<form data-cy="form-searchbike" className="my-5">
					{/* Bike Code */}
					<div
						className={clsx("flex mr-7", {
							"flex-col": isMobileOnly,
							"items-center": !isMobileOnly,
						})}
					>
						<FormElement
							value={sanitizeCode(formFields.bikeCode)}
							placeholder={searchPlaceholder}
							name="bikeCode"
							type="text"
							label={
								readonly
									? t("search_code_read", { ns: "bike" })
									: t("search_code", { ns: "bike" })
							}
							className={clsx({ "w-2/5": !isMobileOnly, "w-full": isMobileOnly })}
							required={true}
							maxLength={BIKE_ID_MAX_LENGTH}
							customRules={[
								{
									key: "bikeCode-empty",
									check: (value: string) => isBikeCodeValid(value),
									message: t("code_min_length", { ns: "form" }),
								},
							]}
							readonly={readonly}
							showErrors={showLocalErrors}
							onValidate={handleValidation}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setFormFields({
									...formFields,
									bikeCode: sanitizeCode(event.target.value),
								})
							}}
						/>
						{!readonly && (
							<div className={clsx("flex", { "py-4": isMobileOnly })}>
								<label
									className={clsx("c-label", {
										"ml-5": !isMobileOnly,
										"mt-2": isMobileOnly,
									})}
								>
									{t("global.or")}{" "}
								</label>

								{!isMoustacheCurrentTheme && (
									<button
										type="button"
										className="flex items-center"
										onClick={scanCode}
									>
										<label
											className={clsx("c-label mr-3 cursor-pointer", {
												"ml-5": !isMobileOnly,
												"ml-2": isMobileOnly,
											})}
										>
											{t("searchBike.scan", { ns: "bike" })}
										</label>
										<ScanIcon size={40} />
									</button>
								)}
								{isMoustacheCurrentTheme && (
									<button
										type="button"
										className="flex items-center"
										onClick={() => setIsQRCodeModalOpen(true)}
									>
										<label
											className={clsx("c-label mr-3 cursor-pointer", {
												"ml-5": !isMobileOnly,
												"ml-2": isMobileOnly,
											})}
										>
											{t("searchBike.scan", { ns: "bike" })}
										</label>
										<BsQrCodeScan size={40} />
									</button>
								)}
							</div>
						)}
					</div>

					{!readonly && (
						<div className="flex mt-5">
							{formEvents.ready && (
								<button
									data-cy="submit-bike-code"
									type="button"
									className="c-btn c-btn--primary px-12"
									onClick={() => handleSubmit(formFields.bikeCode)}
								>
									{t("global.validate")}
								</button>
							)}
							{!formEvents.ready && <div className="c-spinner"></div>}
						</div>
					)}
				</form>

				<QRCodeModal
					isOpen={isQRCodeModalOpen}
					closeModal={() => setIsQRCodeModalOpen(false)}
					onQRCodeScanned={onQRCodeScanned}
				/>

				{apiError && (
					<div className="mb-7">
						<p data-cy="error-api-search-bike" className="text-danger">
							{t(apiError)}
						</p>
						<p data-cy="search-global-bike" className="text-danger">
							{t("search_global_error", { ns: "bike" })}
						</p>
					</div>
				)}
				{bike && <BikeStatusInformation isAdmin={isAdmin} />}
			</div>
		</>
	)
}
